import {IAppDomainConfig} from "./env.configs.interfaces";
import {ApplicationEnvironmentTypeEnum, PortalTypeEnum} from "./configuration-object.interface";
import {UndefinedString} from "../../types/nullable-types";
import {RawLocalStorageKeys} from "../storage/raw-local-storage/raw-local-storage-keys";


function getProductionApiOrigin(): string {
    return 'https://aeroitalia-gateway.azure-api.net/mobileappprod-api';
}

function getStageApiOrigin(): string {
    return 'https://aeroitalia-gateway.azure-api.net/stage-ibe-api';
}

function getTestApiOrigin(): string {
    return 'https://aeroitalia-gateway.azure-api.net/mobileapptest-api';
}

function getBetaApiOrigin(): string {
    return 'https://aeroitalia-gateway.azure-api.net/beta-ibe-api';
}

function getDevApiOrigin(): string {
    return 'https://localhost:5001';
}

function createProductionEnvironment(domainOrigin: string, portalType: PortalTypeEnum): IAppDomainConfig {
    return  {
        environmentType: ApplicationEnvironmentTypeEnum.Production,
        domainOrigin: domainOrigin,
        apiOrigin: getProductionApiOrigin(),
        portalType: portalType,
    };
}

function createB2CProductionEnvironment(domainOrigin: string): IAppDomainConfig {
    return createProductionEnvironment(domainOrigin, PortalTypeEnum.B2C);
}

function createAgencyProductionEnvironment(domainOrigin: string): IAppDomainConfig {
    return createProductionEnvironment(domainOrigin, PortalTypeEnum.Agency);
}

function createStageEnvironment(domainOrigin: string, portalType: PortalTypeEnum): IAppDomainConfig {
    return  {
        environmentType: ApplicationEnvironmentTypeEnum.Stage,
        domainOrigin: domainOrigin,
        apiOrigin: getStageApiOrigin(),
        portalType: portalType
    };
}

function createB2CStageEnvironment(domainOrigin: string): IAppDomainConfig {
    return createStageEnvironment(domainOrigin, PortalTypeEnum.B2C);
}

function createAgencyStageEnvironment(domainOrigin: string): IAppDomainConfig {
    return createStageEnvironment(domainOrigin, PortalTypeEnum.Agency);
}

function createTestEnvironment(domainOrigin: string, portalType: PortalTypeEnum): IAppDomainConfig {
    return   {
        environmentType: ApplicationEnvironmentTypeEnum.Test,
        domainOrigin: domainOrigin,
        apiOrigin: getTestApiOrigin(),
        portalType: portalType
    };
}


function createB2CTestEnvironment(domainOrigin: string): IAppDomainConfig {
    return createTestEnvironment(domainOrigin, PortalTypeEnum.B2C);
}

function createAgencyTestEnvironment(domainOrigin: string): IAppDomainConfig {
    return createTestEnvironment(domainOrigin, PortalTypeEnum.Agency);
}


function createBetaEnvironment(domainOrigin: string, portalType: PortalTypeEnum): IAppDomainConfig {
    return   {
        environmentType: ApplicationEnvironmentTypeEnum.Test,
        domainOrigin: domainOrigin,
        apiOrigin: getBetaApiOrigin(),
        portalType: portalType
    };
}

function createB2CBetaEnvironment(domainOrigin: string): IAppDomainConfig {
    return createBetaEnvironment(domainOrigin, PortalTypeEnum.B2C);
}

function createAgencyBetaEnvironment(domainOrigin: string): IAppDomainConfig {
    return createBetaEnvironment(domainOrigin, PortalTypeEnum.Agency);
}


function createDevEnvironment(options: {domainOrigin: string; apiOrigin: string; portalType?: PortalTypeEnum}): IAppDomainConfig {
    return   {
        environmentType: ApplicationEnvironmentTypeEnum.Development,
        domainOrigin: options.domainOrigin,
        apiOrigin: options.apiOrigin,
        portalType: options.portalType ?? PortalTypeEnum.B2C,
    };
}

export class EnvConfigs {

    constructor() {
        this._currentDomain = this._loadCurrentDomain();
    }

    private readonly _currentDomain: IAppDomainConfig;

    private _getNgrokHeaders(): Record<string, string> {
        return {
            'ngrok-skip-browser-warning': 'true'
        }
    }

    get ngrokHeadersForWebApi(): Record<string, string> {
        if(process.env.REACT_APP_USE_NGROK_HEADERS_FOR_WEBAPI === 'true') {
            return this._getNgrokHeaders();
        }
        return {};
    }


    public readonly appDomains = [
        createB2CProductionEnvironment('http://localhost'),
        createB2CProductionEnvironment('capacitor://localhost'),

        createB2CProductionEnvironment('https://book.aeroitalia.com'),
        createB2CProductionEnvironment('https://flights.aeroitalia.com'),
        createAgencyProductionEnvironment('https://agency.aeroitalia.com'),
        createAgencyProductionEnvironment('https://corporate.aeroitalia.com'),

        createB2CStageEnvironment('https://stage-ibe.aeroitalia.com'),
        createAgencyStageEnvironment('https://agency-stage-ibe.aeroitalia.com'),


        createB2CTestEnvironment('https://test-ibe.aeroitalia.com'),
        createAgencyTestEnvironment('https://agency-test-ibe.aeroitalia.com'),

        createB2CBetaEnvironment('https://beta-test-ibe.aeroitalia.com/'),
        createAgencyBetaEnvironment('https://agency-beta-test-ibe.aeroitalia.com/'),



        createDevEnvironment({domainOrigin: 'http://localhost:8100', apiOrigin: getDevApiOrigin()}),
        createDevEnvironment({domainOrigin: 'http://localhost:8101', apiOrigin: getDevApiOrigin()}),
        createDevEnvironment({domainOrigin: 'http://localhost:9034', apiOrigin: getDevApiOrigin()}),
        createDevEnvironment({domainOrigin: 'https://18b7-79-115-63-81.ngrok-free.app', apiOrigin: 'https://correct-annually-cod.ngrok-free.app'}),
        createDevEnvironment({domainOrigin: 'http://localhost:3000', apiOrigin: getTestApiOrigin()}) // this is for DEV mode on Mac

        //createB2CTestEnvironment('https://nice-water-0705a5003.2.azurestaticapps.net'),
        //createB2CTestEnvironment('https://stage-mobile-a8f3hdhferhjfteu.z03.azurefd.net'),
        //createB2CTestEnvironment('https://ibereskinrest.z6.web.core.windows.net'),
        //createB2CTestEnvironment('https://test-ibe-reskin.azureedge.net'),
        //createB2CProductionEnvironment('https://white-desert-0dfbc4103.2.azurestaticapps.net'),
    ];


    get airlineWebapiOrigin(): string {
        return this._currentDomain.apiOrigin;
    }

    readonly enableCheckin = true; //process.env.REACT_APP_ENABLE_CHECKIN === 'true';
    readonly enableManageMyBooking = true; //process.env.REACT_APP_ENABLE_MANAGE_MY_BOOKING === 'true';
    readonly enableLogin = process.env.REACT_APP_ENABLE_LOGIN === 'true';
    readonly byPassCheckInRestrictions = false;
    private readonly _preferredApiOrigin: UndefinedString = process.env.REACT_APP_NGROK_WEBAPI_URL || process.env.REACT_APP_PREFERRED_API;

    private _findDomainByCurrentLocation(): IAppDomainConfig {
        let currentLocation = new URL(window.location.href);

        for(let domain of this.appDomains) {
            const domainOrigin = new URL(domain.domainOrigin);
            if(currentLocation.origin.toLowerCase() === domainOrigin.origin.toLowerCase()) {
                return domain;
            }
        }

        const firstProdDomain = this.appDomains.find(d => d.environmentType === ApplicationEnvironmentTypeEnum.Production);
        if(firstProdDomain) {
            return firstProdDomain;
        }

        throw new Error('No domain configuration found for current domain');
    }

    private _applyPreferredApiOrigin(domain: IAppDomainConfig) {
        if(this._preferredApiOrigin) {
            return {
                ...domain,
                apiOrigin: this._preferredApiOrigin,
                environmentType: ApplicationEnvironmentTypeEnum.Development
            }
        }

        return domain;

    }
    private _loadCurrentDomain(): IAppDomainConfig {
        const po = localStorage.getItem(RawLocalStorageKeys.envConfigsPreferredApiOrigin)?.toLowerCase();
        if(po) {
            const preferredDomain = this.appDomains.find(d => d.apiOrigin.toLowerCase() === po);
            if(preferredDomain) {
                return this._applyPreferredApiOrigin(preferredDomain);
            }
        }

        return this._applyPreferredApiOrigin(this._findDomainByCurrentLocation());
    }

    getInitialApiOrigin(): string {
        return this._findDomainByCurrentLocation().apiOrigin;
    }

    get isProduction(): boolean {
        return this._currentDomain.environmentType === ApplicationEnvironmentTypeEnum.Production;
    }

    get isStage(): boolean {
        return this._currentDomain.environmentType === ApplicationEnvironmentTypeEnum.Stage;
    }

    get currentEnvironmentType(): ApplicationEnvironmentTypeEnum {
        return this._currentDomain.environmentType;
    }

    get portalType(): PortalTypeEnum {
        return this._currentDomain.portalType;
    }
}


